import keyBinder from 'shared/lib/keyBinder';
import FullScreenCuesheetActions from 'flux/actions/cuesheet.fullscreen/FullScreenCuesheetActions';
import KeyBindingActions from 'flux/actions/KeyBindingActions';
import RTEActions from 'flux/actions/RTEActions';
import {
  InlineStyleHotkeyBindings,
  KeyScopes,
} from 'flux/stores/cuesheet/CuesheetConstants';
import { AutoScrollSpeeds } from 'shared/constants/FeaturedEditorConstants';

const bindings = [
  ['space', '_handleSpaceKeyPress'],
  ['shift+space', '_handleShiftSpaceKeyPress'],
  ['up', '_handleUpArrowKeyPress'],
  ['down', '_handleDownArrowKeyPress'],
  ['left', '_selectRowAbove'],
  ['right', '_selectRowBelow'],
  ['shift+left', '_selectRowAboveWithContent'],
  ['shift+right', '_selectRowBelowWithContent'],
  ['option+=', '_incrementZoom'],
  ['option+-', '_decrementZoom'],
  ['alt+]', '_incrementLineHeight'],
  ['alt+[', '_decrementLineHeight'],
  ['shift+command+left, shift+ctrl+left', '_handleNarrowPrompterWidth'],
  ['shift+command+right, shift+ctrl+right', '_handleWidenPrompterWidth'],
  ['command+right, ctrl+right, command+left, ctrl+left', '_preventDefault'],
  ['shift+up', '_handleScrollToTop'],
  ['shift+down', '_handleScrollToBottom'],
  ['-', '_handleStopAutoScroll'],
];

const editModeBindings = InlineStyleHotkeyBindings.map((binding) =>
  binding.concat(KeyScopes.FullScreenEditing),
);
const fullScreenToggleBinding = [
  'control+command+f',
  '_handleToggleFullScreenPress',
];

export default {
  _incrementZoom() {
    FullScreenCuesheetActions.incrementZoom();
  },

  _decrementZoom() {
    FullScreenCuesheetActions.decrementZoom();
  },

  _incrementLineHeight() {
    FullScreenCuesheetActions.incrementLineHeight();
  },

  _decrementLineHeight() {
    FullScreenCuesheetActions.decrementLineHeight();
  },

  _enableFullScreenHotKeys() {
    keyBinder.setScope(KeyScopes.FullScreen);
    this._bindFullScreenKeys();

    this.listenTo(
      KeyBindingActions.bindFullScreenKeys,
      this.onBindFullScreenKeys,
    );
    this.listenTo(
      KeyBindingActions.unbindFullScreenKeys,
      this.onUnbindFullScreenKeys,
    );
  },

  _enableToggleFullScreenHotKeys() {
    keyBinder.setScope(KeyScopes.FullScreen);
    this._bindToggleFullScreenKeys();

    this.listenTo(
      KeyBindingActions.bindToggleFullScreenKeys,
      this.onBindToggleFullScreenKeys,
    );
    this.listenTo(
      KeyBindingActions.unbindToggleFullScreenKeys,
      this.onUnbindToggleFullScreenKeys,
    );
  },

  _disableToggleFullScreenHotKeys() {
    this._unbindToggleFullScreenKeys();
  },

  _disableFullScreenHotKeys() {
    this._unbindFullScreenKeys();
  },

  onBindFullScreenKeys() {
    this._bindFullScreenKeys();
  },

  onUnbindFullScreenKeys() {
    this._unbindFullScreenKeys();
  },

  onBindToggleFullScreenKeys() {
    this._bindToggleFullScreenKeys();
  },

  onUnbindToggleFullScreenKeys() {
    this._unbindToggleFullScreenKeys();
  },

  _handleSpaceKeyPress(e) {
    e.preventDefault();
    FullScreenCuesheetActions.toggleAutoScrolling();
  },

  _handleShiftSpaceKeyPress(e) {
    e.preventDefault();
    FullScreenCuesheetActions.toggleAutoScrolling();
  },

  _handleUpArrowKeyPress(e) {
    e.preventDefault();
    FullScreenCuesheetActions.decreaseFeaturedAutoScrollSpeed();
  },

  _handleDownArrowKeyPress(e) {
    e.preventDefault();
    FullScreenCuesheetActions.increaseFeaturedAutoScrollSpeed();
  },

  _selectRowAbove() {
    const { rowOrder } = this.props;

    const currentRowIndex = this._activeRowIndex;

    if (currentRowIndex === 0) return;

    const nextRowIndex = currentRowIndex - 1;
    const nextRowId = rowOrder[nextRowIndex];

    FullScreenCuesheetActions.scrollToRowIdInPrompter(nextRowId);
  },

  _selectRowBelow() {
    const { rowOrder } = this.props;

    const currentRowIndex = this._activeRowIndex;

    if (currentRowIndex === rowOrder.length - 1) return;

    const nextRowIndex = currentRowIndex + 1;
    const nextRowId = rowOrder[nextRowIndex];

    FullScreenCuesheetActions.scrollToRowIdInPrompter(nextRowId);
  },

  _selectRowAboveWithContent() {
    const { columnId, itemSetId, rowOrder, rowMap } = this.props;

    const currentRowIndex = this._activeRowIndex;

    if (currentRowIndex === 0) return;

    const nextRowOrder = rowOrder.slice(0, currentRowIndex).reverse();

    const nextRowId = nextRowOrder.find((rowId) => {
      const { item } = rowMap[itemSetId].rows[rowId];
      return item.cells[columnId] && item.cells[columnId].text;
    });

    if (!nextRowId) return;

    FullScreenCuesheetActions.scrollToRowIdInPrompter(nextRowId);
  },

  _selectRowBelowWithContent() {
    const { columnId, itemSetId, rowOrder, rowMap } = this.props;

    const currentRowIndex = this._activeRowIndex;

    if (currentRowIndex === rowOrder.length - 1) return;

    const nextRowOrder = rowOrder.slice(currentRowIndex + 1);

    const nextRowId = nextRowOrder.find((rowId) => {
      const { item } = rowMap[itemSetId].rows[rowId];
      return item.cells[columnId] && item.cells[columnId].text;
    });

    if (!nextRowId) return;

    FullScreenCuesheetActions.scrollToRowIdInPrompter(nextRowId);
  },

  _handleToggleFullScreenPress(e) {
    e.preventDefault();
    FullScreenCuesheetActions.toggleFullScreen();
  },

  _handleNarrowPrompterWidth(e) {
    e.preventDefault();
    FullScreenCuesheetActions.narrowPrompterWidth();
  },

  _handleWidenPrompterWidth(e) {
    e.preventDefault();
    FullScreenCuesheetActions.widenPrompterWidth();
  },

  _getRowIndexByRowId(rowId) {
    const { rowOrder } = this.props;
    return rowOrder.findIndex((id) => id === rowId);
  },

  _getRowIdByRowIndex(rowIndex) {
    const { rowOrder } = this.props;
    return rowOrder[rowIndex];
  },

  _preventDefault(e) {
    e.preventDefault();
  },

  /**
   * ### _bindFullScreenKeys()
   *
   * Binds cue grid FullScreen keys
   *
   */
  _bindFullScreenKeys() {
    //Don't create duplicate bindings
    if (this.state.keysBound) return;

    const keyBindings = bindings.concat(editModeBindings);

    /*
     * TODO: Figure out a good solution to prevent duplicate bindings.
     *
     * When the bind action is called twice it will rebind the keys causing
     * what ever action that was bound the first time and the second time to
     * be called twice. For instance the FullScreen keys were being bound
     * twice and so when you would hit the left arrow key once it would
     * move it over twice.
     */
    keyBindings.forEach((binding) => {
      const [key, handler, scope] = binding;
      keyBinder.assign(key, scope || KeyScopes.FullScreen, this[handler]);
    });

    this._bindPrompterSpeedKeys();

    this.setState({ keysBound: true });
  },

  /**
   *
   * ### _unbindFullScreenKeys()
   *
   */
  _unbindFullScreenKeys() {
    bindings.forEach((binding) => {
      const [key, handler, scope] = binding;
      keyBinder.unbind(key, scope || KeyScopes.FullScreen, this[handler]);
    });

    this._unBindPrompterSpeedKeys();

    this.setState({ keysBound: false });
  },

  _bindToggleFullScreenKeys() {
    if (this.state.toggleFullScreenBound) return;

    const [key, handler] = fullScreenToggleBinding;
    keyBinder.assign(key, KeyScopes.FullScreen, this[handler]);

    this.setState({ toggleFullScreenBound: true });
  },

  _unbindToggleFullScreenKeys() {
    const [key, handler] = fullScreenToggleBinding;
    keyBinder.unbind(key, KeyScopes.FullScreen, this[handler]);

    this.setState({ toggleFullScreenBound: false });
  },

  _handleCustomInlineStyleOneKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-black0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleTwoKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-red0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleThreeKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-red0']);
  },

  _handleCustomInlineStyleFourKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-green0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleFiveKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-green0']);
  },

  _handleCustomInlineStyleSixKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-yellow0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleSevenKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-yellow0']);
  },

  _handleCustomInlineStyleEightKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-blue0', 'bgcolor-white']);
  },

  _handleCustomInlineStyleNineKeyPress(e) {
    e.preventDefault();
    RTEActions.applyInlineFormatting(['color-blue0']);
  },

  _handleScrollToTop(e) {
    e.preventDefault();

    const currentRowIndex = this._activeRowIndex;

    if (currentRowIndex === 0) return;

    FullScreenCuesheetActions.scrollToRowIdInPrompter(0);
  },

  _handleScrollToBottom(e) {
    e.preventDefault();

    const { rowOrder } = this.props;

    const currentRowIndex = this._activeRowIndex;

    if (currentRowIndex === 0) return;

    const lastRowIndex = rowOrder.length - 1;
    const lastRowId = rowOrder[lastRowIndex];

    FullScreenCuesheetActions.scrollToRowIdInPrompter(lastRowId);
  },

  _handleStopAutoScroll(e) {
    e.preventDefault();
    FullScreenCuesheetActions.stopScrolling();
  },

  _handleSetAutoScrollSpeed(speedIndex) {
    FullScreenCuesheetActions.setFeaturedAutoScrollSpeed(speedIndex);
  },

  _createPrompterSpeedBindings() {
    return AutoScrollSpeeds.map((config, index) => ({
      key: config.hotkey,
      autoScrollSpeedIndex: index,
    }));
  },

  _bindPrompterSpeedKeys() {
    const hotkeyBindings = this._createPrompterSpeedBindings();

    hotkeyBindings.forEach((binding) => {
      const { key, autoScrollSpeedIndex } = binding;

      /** Define shortcuts with FullScreen key scope */
      keyBinder.assign(key, KeyScopes.FullScreen, (e) => {
        e.preventDefault();

        this._handleSetAutoScrollSpeed(autoScrollSpeedIndex);
      });
    });
  },

  _unBindPrompterSpeedKeys() {
    const hotkeyBindings = this._createPrompterSpeedBindings();

    hotkeyBindings.forEach((binding) => {
      const { key, autoScrollSpeedIndex } = binding;

      /** Define shortcuts with FullScreen key scope */
      keyBinder.unbind(key, KeyScopes.FullScreen, () =>
        this._handleSetAutoScrollSpeed(autoScrollSpeedIndex),
      );
    });
  },
};
